import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog without causing page jump.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  // sauvegarde/restauration styles pour le scroll-lock propre
  #prevScrollBehavior = '';
  #savedBodyPaddingRight = '';
  #scrollbarWidth = 0;

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;
    if (!minWidth && !maxWidth) return;

    const w = window.innerWidth;
    if (w < minWidth || w > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  /**
   * Calcule la largeur de la scrollbar pour compenser le layout shift.
   */
  #computeScrollbarWidth() {
    this.#scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    if (this.#scrollbarWidth < 0) this.#scrollbarWidth = 0;
  }

  /**
   * Active un scroll-lock qui ne déplace pas la page (pas de position:fixed/top:-scrollY).
   */
  #lockScroll() {
    // mémorise le comportement de scroll courant (pour le restaurer ensuite)
    this.#prevScrollBehavior = document.documentElement.style.scrollBehavior || '';
    document.documentElement.style.scrollBehavior = 'auto';

    // compense la disparition de la scrollbar pour éviter un décalage horizontal
    this.#computeScrollbarWidth();
    this.#savedBodyPaddingRight = getComputedStyle(document.body).paddingRight || '';
    const currentPR = parseFloat(this.#savedBodyPaddingRight) || 0;
    if (this.#scrollbarWidth > 0) {
      document.body.style.paddingRight = String(currentPR + this.#scrollbarWidth) + 'px';
    }

    // bloque le scroll sans bouger la position
    document.documentElement.classList.add('dialog-open'); // cf. CSS: html.dialog-open { overflow: hidden; }
    document.documentElement.setAttribute('scroll-lock', ''); // compat avec écouteur 'toggle' ci-dessous
  }

  /**
   * Désactive le scroll-lock et restaure les styles.
   */
  #unlockScroll() {
    document.documentElement.classList.remove('dialog-open');
    document.documentElement.removeAttribute('scroll-lock');
    document.documentElement.style.scrollBehavior = this.#prevScrollBehavior || '';

    // restaure le padding-right d'origine
    document.body.style.paddingRight = this.#savedBodyPaddingRight || '';
    this.#savedBodyPaddingRight = '';
    this.#scrollbarWidth = 0;
  }

  /**
   * Shows the dialog (no page jump).
   */
  showDialog() {
    const { dialog } = this.refs;
    if (dialog.open) return;

    this.#lockScroll();

    dialog.showModal();
    this.dispatchEvent(new DialogOpenEvent());

    // listeners au tick suivant (évite une race condition si showDialog est appelé dans un handler de clic)
    setTimeout(() => {
      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog (restores styles cleanly).
   */
  closeDialog = async () => {
    const { dialog } = this.refs;
    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    dialog.classList.add('dialog-closing');

    await onAnimationEnd(dialog, undefined, { subtree: false });

    // restaure les styles de scroll avant de fermer le dialog
    this.#unlockScroll();

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;
    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

/* Events */
export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }
  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }
  static eventName = 'dialog:close';
}

/**
 * Preserve legacy behavior for <dialog>/<details> elements with [scroll-lock] attribute.
 * We mirror the lock state on <html> via the attribute, but actual overflow control
 * is handled by .dialog-open class (CSS).
 */
document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDialogElement || event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
          document.documentElement.classList.add('dialog-open');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
          document.documentElement.classList.remove('dialog-open');
        }
      }
    }
  },
  { capture: true }
);
